import {
  formatParisDate,
  OPERATION_KIND,
  OPERATION_KINDS,
  OPERATION_LIFECYCLE,
  operationKindSchema,
  type OperationDetail,
  type OperationInput,
  type OperationKind,
  type OperationPreviewBody,
  type RenewalDiscountOperationInput,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useState } from 'react';
import { useUnsavedChangesGuard } from '@/components/forms/unsaved-changes-guard.js';
import { OriginBadges } from '@/components/filters/origin-badge.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import {
  archiveOperation,
  createOperation,
  fetchOperationDetail,
  previewOperation,
  updateOperation,
} from './api.js';
import { OptionCards } from './option-cards.js';
import { ArchivedBanner, archiveDescription, mutationErrorMessage } from './operation-ui.js';
import { OperationForm } from './operation-form.js';
import { formatCountLabel } from '../subscriptions/money.js';
import { OperationOrdersTab } from './operation-orders-tab.js';
import { RenewalDiscountForm } from './renewal-discount-form.js';
import { RenewalDiscountPage, type RenewalTab } from './renewal-discount-page.js';

type OperationTab = 'reglages' | 'commandes' | 'apercu' | 'suivi';

const KIND_OPTIONS = OPERATION_KINDS.map((kind) => ({
  value: kind,
  label: OPERATION_KIND[kind].label,
  description: OPERATION_KIND[kind].description,
}));

/** Une passe Recharge tourne : la fiche est relue toutes les 2 s (compteurs, aperçu). */
function hasRunningRenewalRun(detail: OperationDetail | undefined): boolean {
  return detail?.renewalDiscount?.lastRuns.some((run) => run.status === 'running') ?? false;
}

/** Recherche par défaut de `/operations` (la route a des paramètres à valeur par défaut). */
const OPERATIONS_LIST_SEARCH = { page: 1, pageSize: 50, sortDir: 'desc' } as const;

function readRecord(rawSearch: unknown): Record<string, unknown> {
  return typeof rawSearch === 'object' && rawSearch !== null ? (rawSearch as Record<string, unknown>) : {};
}

function readTab(rawSearch: unknown): OperationTab {
  const search = readRecord(rawSearch);
  return search.tab === 'commandes' || search.tab === 'apercu' || search.tab === 'suivi' ? search.tab : 'reglages';
}

/** Type choisi à la création (`?type=`, `routes/operation-new.tsx`) ; absent = produit offert. */
function readKind(rawSearch: unknown): OperationKind {
  const parsed = operationKindSchema.safeParse(readRecord(rawSearch).type);
  return parsed.success ? parsed.data : 'free_product';
}

export interface OperationPageProps {
  /** `true` sur `/operations/nouvelle` : formulaire vide, pas d'onglet Commandes. */
  isNew?: boolean;
}

/**
 * Page d'une opération commerciale (`02` §10) — une page dédiée, plus un
 * dialogue : en-tête (nom, état, origines, nombre de commandes) et deux
 * onglets, Réglages et Commandes.
 */
export function OperationPage({ isNew = false }: OperationPageProps): JSX.Element {
  const params: { id?: string } = useParams({ strict: false });
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = isNew ? '' : (params.id ?? '');
  const tab = readTab(rawSearch);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const guard = useUnsavedChangesGuard(formDirty);
  const newKind = readKind(rawSearch);

  const detailQuery = useQuery({
    queryKey: queryKeys.operations.detail(id),
    queryFn: () => fetchOperationDetail(id),
    enabled: !isNew && id !== '',
    refetchInterval: (query) => (hasRunningRenewalRun(query.state.data) ? 2_000 : false),
  });

  // Erreur affichée dans le panneau de simulation (`isPreviewError`), pas en toast.
  const previewMutation = useMutation({
    mutationFn: (body: OperationPreviewBody) => previewOperation(body),
  });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.operations.all });
  }

  const saveMutation = useMutation({
    mutationFn: (input: OperationInput | RenewalDiscountOperationInput) =>
      isNew ? createOperation(input) : updateOperation(id, input),
    onSuccess: (saved) => {
      toast.success(isNew ? 'Opération créée.' : 'Opération enregistrée.');
      // La fiche renvoyée devient aussitôt la référence du formulaire, sans
      // attendre le rechargement : « Enregistrer » redevient inactif.
      queryClient.setQueryData(queryKeys.operations.detail(saved.id), saved);
      invalidate();
      if (isNew) {
        guard.allowNextNavigation();
        void navigate({ to: `/operations/${saved.id}` });
      }
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveOperation(id),
    onSuccess: () => {
      toast.success('Opération archivée.');
      invalidate();
      setArchiveOpen(false);
      guard.allowNextNavigation();
      void navigate({ to: '/operations', search: OPERATIONS_LIST_SEARCH });
    },
  });

  const submitError = saveMutation.isError ? mutationErrorMessage(saveMutation.error) : null;

  function setNewKind(next: OperationKind): void {
    // L'échec d'enregistrement portait sur l'autre formulaire.
    saveMutation.reset();
    // Changer de type remonte un autre formulaire : la garde de sortie protège la saisie.
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, type: next === 'free_product' ? undefined : next }),
      replace: true,
    });
  }

  function setTab(next: OperationTab): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }),
      replace: true,
    });
  }

  if (!isNew && detailQuery.isPending) {
    return (
      <Page width="detail">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-8 w-72 max-w-full" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex max-w-[880px] flex-col gap-6">
          <Skeleton className="h-64 w-full rounded-lg" />
          <Skeleton className="h-72 w-full rounded-lg" />
        </div>
      </Page>
    );
  }

  if (!isNew && (detailQuery.isError || !detailQuery.data)) {
    return (
      <Page width="detail">
        <ErrorState
          message={
            detailQuery.error instanceof ApiError
              ? detailQuery.error.message
              : 'Opération introuvable.'
          }
          detail={detailQuery.error instanceof ApiError ? detailQuery.error.detail : undefined}
          onRetry={() => void detailQuery.refetch()}
        />
      </Page>
    );
  }

  const detail = isNew ? undefined : detailQuery.data;

  if (detail?.kind === 'renewal_discount' && detail.renewalDiscount !== null) {
    return (
      <RenewalDiscountPage
        detail={detail}
        config={detail.renewalDiscount}
        tab={tab === 'commandes' ? 'reglages' : tab}
        onTabChange={(next: RenewalTab) => {
          setTab(next);
        }}
      />
    );
  }

  const isNewRenewal = isNew && newKind === 'renewal_discount';

  return (
    <Page width={isNew ? 'form' : 'detail'}>
      <PageHeader
        backLink={{
          label: 'Retour aux opérations',
          to: '/operations',
          search: OPERATIONS_LIST_SEARCH,
        }}
        title={detail?.name ?? 'Nouvelle opération'}
        status={
          detail ? <StatusBadge registry={OPERATION_LIFECYCLE} code={detail.lifecycle} /> : null
        }
        description={
          detail ? (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <OriginBadges codes={detail.stores} emptyLabel="Toutes les origines" />
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">
                du {formatParisDate(new Date(detail.period.start))}
                {detail.period.end === null
                  ? ' (sans fin)'
                  : ` au ${formatParisDate(new Date(detail.period.end))}`}
              </span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">
                {formatCountLabel(detail.matchedOrdersCount, 'commande', 'commandes')}
              </span>
            </span>
          ) : isNewRenewal ? (
            'Posez automatiquement une remise sur le prochain renouvellement d’abonnement Recharge de chaque client, pendant une période donnée.'
          ) : (
            'Ajoutez automatiquement des produits aux commandes qui remplissent certaines conditions, pendant une période donnée.'
          )
        }
      />

      {detail?.archived ? <ArchivedBanner kind="free_product" /> : null}

      {isNew ? null : (
        // Activation manuelle : le focus rendu à un onglet (fermeture de la garde de sortie) ne doit pas le sélectionner.
        <Tabs
          activationMode="manual"
          value={tab}
          onValueChange={(next) => {
            setTab(next === 'commandes' ? 'commandes' : 'reglages');
          }}
        >
          <TabsList>
            <TabsTrigger value="reglages">Réglages</TabsTrigger>
            <TabsTrigger value="commandes">Commandes</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isNew ? (
        <div className="w-full max-w-[880px]">
          <OptionCards
            name="operation-kind"
            legend="Type d’opération"
            options={KIND_OPTIONS}
            value={newKind}
            onChange={setNewKind}
            columns={2}
          />
        </div>
      ) : null}

      {isNewRenewal ? (
        <RenewalDiscountForm
          detail={undefined}
          isSaving={saveMutation.isPending}
          submitError={submitError}
          onDirtyChange={setFormDirty}
          onSubmit={(input) => {
            saveMutation.mutate(input);
          }}
        />
      ) : tab === 'commandes' && !isNew ? (
        <OperationOrdersTab operationId={id} />
      ) : (
        <OperationForm
          detail={detail}
          isSaving={saveMutation.isPending}
          submitError={submitError}
          onDirtyChange={setFormDirty}
          onSubmit={(input) => {
            saveMutation.mutate(input);
          }}
          onPreview={(input) => {
            previewMutation.mutate({ conditions: input.conditions, stores: input.stores });
          }}
          preview={previewMutation.data}
          isPreviewing={previewMutation.isPending}
          isPreviewError={previewMutation.isError}
          previewErrorMessage={
            previewMutation.error instanceof ApiError ? previewMutation.error.message : undefined
          }
          {...(isNew || detail?.archived
            ? {}
            : {
                onArchive: (): void => {
                  archiveMutation.reset();
                  setArchiveOpen(true);
                },
              })}
        />
      )}

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (open) archiveMutation.reset();
          setArchiveOpen(open);
        }}
        title={`Archiver « ${detail?.name ?? ''} » ?`}
        description={archiveDescription('free_product', 0)}
        confirmLabel="Archiver l’opération"
        isConfirming={archiveMutation.isPending}
        error={archiveMutation.isError ? mutationErrorMessage(archiveMutation.error) : null}
        onConfirm={() => {
          archiveMutation.mutate();
        }}
      />
      {guard.dialog}
    </Page>
  );
}
